import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  Signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CurrentScopeFacade } from '@principle-theorem/ng-principle-shared';
import {
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
  validFormControlChanges$,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { Brand } from '@principle-theorem/principle-core';
import {
  ICustomReportFilter,
  IMeasureMetadata,
  isDataPointValue,
  ReportFilterComparison,
  ReportFilterOption,
} from '@principle-theorem/principle-core/interfaces';
import {
  CustomReportFilter,
  QueryOptionResolver,
  ReportBuilderDataSourceInstance,
} from '@principle-theorem/reporting';
import {
  Firestore,
  IGroup,
  ISelectOption,
  snapshot,
} from '@principle-theorem/shared';
import { compact, first, groupBy, isEqual, isString, sortBy } from 'lodash';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

interface ISelectFilterForm {
  comparison: ReportFilterComparison;
  values: ReportFilterOption[];
}

@Component({
  selector: 'pr-edit-report-filter-select',
  templateUrl: './edit-report-filter-select.component.html',
  styleUrls: ['./edit-report-filter-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatChipsModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    NgCommonModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
  ],
})
export class EditReportFilterSelectComponent {
  private _scope = inject(CurrentScopeFacade);
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  searchCtrl = new TypedFormControl<string>('');
  selectedCtrl = new TypedFormControl<ReportFilterOption[]>([]);
  formGroup = new TypedFormGroup<ISelectFilterForm>({
    comparison: new TypedFormControl<ReportFilterComparison>(
      ReportFilterComparison.IsEqualTo,
      Validators.required
    ),
    values: new TypedFormControl<ReportFilterOption[]>([], Validators.required),
  });

  dataSource = input.required<ReportBuilderDataSourceInstance>();
  filter = input.required<ICustomReportFilter>();
  filterChange = output<Partial<ICustomReportFilter>>();

  comparisons: ISelectOption<ReportFilterComparison>[] = [
    { value: ReportFilterComparison.IsEqualTo, label: 'Is' },
    { value: ReportFilterComparison.IsNotEqualTo, label: 'Is not' },
  ];
  propertyLabel: Signal<string>;
  options: Signal<ReportFilterOption[]>;
  filteredOptions: Signal<IGroup<ReportFilterOption, string>[]>;
  selectedOptions = signal<ReportFilterOption[]>([]);

  constructor() {
    const property$ = combineLatest([
      toObservable(this.dataSource),
      toObservable(this.filter).pipe(
        distinctUntilChanged((a, b) => a.id === b.id)
      ),
    ]).pipe(
      map(([dataSource, filter]) =>
        CustomReportFilter.getProperty(dataSource.factTable, filter)
      )
    );
    const propertyLabel$ = property$.pipe(
      map((property) => property?.metadata.label ?? 'Value')
    );
    this.propertyLabel = toSignal(propertyLabel$, { initialValue: 'Value' });

    const options$ = property$.pipe(
      switchMap((property) => this._loadOptions(property?.metadata))
    );
    this.options = toSignal(options$, { initialValue: [] });

    const search = toSignal(validFormControlChanges$(this.searchCtrl), {
      initialValue: '',
    });
    this.filteredOptions = computed(() =>
      this._getFilteredOptions(this.options(), search())
    );

    combineLatest([toObservable(this.filter), options$])
      .pipe(distinctUntilChanged(isEqual), takeUntilDestroyed())
      .subscribe(
        ([filter, options]: [ICustomReportFilter, ReportFilterOption[]]) =>
          this._patchFormGroup(filter, options)
      );

    validFormGroupChanges$(this.formGroup)
      .pipe(takeUntilDestroyed())
      .subscribe((formValue) => {
        this.filterChange.emit({
          ...formValue,
          dateType: undefined,
          relativeOffset: undefined,
        });
      });
  }

  getComparisonLabel(comparison: ReportFilterComparison): string {
    return CustomReportFilter.getComparisonDisplay(comparison, true);
  }

  toggleSelectAll(selectAll: boolean): void {
    if (selectAll) {
      const options = this.filteredOptions();
      this.formGroup.controls.values.patchValue([...options]);
      return;
    }
    this.formGroup.controls.values.patchValue([]);
  }

  compareWithFn(a?: ReportFilterOption, b?: ReportFilterOption): boolean {
    if (!a || !b) {
      return false;
    }
    return a.value === b.value;
  }

  private async _loadOptions(
    metadata?: IMeasureMetadata
  ): Promise<ReportFilterOption[]> {
    if (!metadata) {
      return [];
    }
    const brand = await snapshot(this._scope.currentBrand$);
    if (!brand) {
      return [];
    }
    const organisation = await Firestore.getDoc(Brand.organisationRef(brand));
    return QueryOptionResolver.resolve(
      { brand, organisation },
      metadata.optionResolver
    );
  }

  private _getSelectedOptionsFromFilter(
    options: ReportFilterOption[],
    filter: ICustomReportFilter
  ): ReportFilterOption[] {
    const values = filter.values
      .map((value) => value.value)
      .filter((value) => isDataPointValue(value));
    return options.filter((option) => values.includes(option.value));
  }

  private _getFilteredOptions(
    allOptions: ReportFilterOption[],
    search?: string
  ): IGroup<ReportFilterOption, string>[] {
    const groupedOptions = Object.entries(
      groupBy(allOptions, (option) => (option.isDeleted ? 'Deleted' : ''))
    ).map(([group, items]) => ({
      group,
      items: sortBy(items, (option) => option.label.toLowerCase()),
    }));

    const filteredOptions = this._filterOptions(groupedOptions, search);
    return sortBy(filteredOptions, (option) => option.group.toLowerCase());
  }

  private _filterOptions(
    options: IGroup<ReportFilterOption, string>[],
    search?: string | ReportFilterOption
  ): IGroup<ReportFilterOption, string>[] {
    if (!search || !isString(search)) {
      return options;
    }
    const searchValue = search?.trim().toLowerCase();

    return compact(
      options.map((option) => {
        const items = option.items.filter((item) =>
          item.label.toLowerCase().includes(searchValue)
        );

        if (!items.length) {
          return;
        }

        return {
          ...option,
          items,
        };
      })
    );
  }

  private _patchFormGroup(
    filter: ICustomReportFilter,
    options: ReportFilterOption[]
  ): void {
    this.formGroup.patchValue({
      comparison: this._getValidComparison(filter),
      values: this._getSelectedOptionsFromFilter(options, filter),
    });
  }

  private _getValidComparison(
    filter: ICustomReportFilter
  ): ReportFilterComparison {
    const validComparisons = this.comparisons.map(
      (comparison) => comparison.value
    );
    const comparison = CustomReportFilter.getComparison(filter);
    if (comparison && validComparisons.includes(comparison)) {
      return comparison;
    }
    return first(validComparisons) ?? ReportFilterComparison.IsEqualTo;
  }
}
